import { useEffect, Suspense, lazy, useState } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import toast from 'react-hot-toast';
import ErrorBoundary from './components/ErrorBoundary';
import WhatsAppFloatingButton from './components/WhatsAppFloatingButton';
import { useMetaTags } from './hooks/useMetaTags';
import { X } from 'lucide-react';

const VISWANATH_POPUP_KEY = 'viswanath_serenity_popup_shown';
const VISWANATH_SERENITY_IMAGE = '/viswanath-serenity.png';

// Route guards - keep these non-lazy as they're small and needed immediately
import AdminRouteGuard from './components/AdminRouteGuard';
import AgentRouteGuard from './components/AgentRouteGuard';
import ClientRouteGuard from './components/ClientRouteGuard';

// Lazy load all page components for better code splitting and faster initial load
const Home = lazy(() => import('./pages/client/Home'));
const Buy = lazy(() => import('./pages/client/Buy'));
const Rent = lazy(() => import('./pages/client/Rent'));
const Sell = lazy(() => import('./pages/client/Sell'));
const Agent = lazy(() => import('./pages/client/Agent'));
const PropertyDetails = lazy(() => import('./pages/client/PropertyDetails'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const About = lazy(() => import('./pages/client/About'));
const Host = lazy(() => import('./pages/client/Host'));
const Community = lazy(() => import('./pages/client/Community'));
const PrivacyPolicy = lazy(() => import('./pages/client/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/client/TermsOfService'));
const Blogs = lazy(() => import('./pages/client/Blogs'));
const ContactUs = lazy(() => import('./pages/client/ContactUs'));
const AccountDeletion = lazy(() => import('./pages/client/AccountDeletion'));
const MyBookings = lazy(() => import('./pages/client/MyBookings'));
const MyInquiries = lazy(() => import('./pages/client/MyInquiries'));
const EmailVerification = lazy(() => import('./pages/EmailVerification'));
const AgentAssignments = lazy(() => import('./pages/agent/AgentAssignments'));
const AgentDashboard = lazy(() => import('./pages/agent/AgentDashboard'));
const Profile = lazy(() => import('./pages/client/Profile'));
const Wishlist = lazy(() => import('./pages/Wishlist'));
const Notifications = lazy(() => import('./pages/Notifications'));
const SellerDashboard = lazy(() => import('./pages/seller/SellerDashboard'));
const SellerInquiries = lazy(() => import('./pages/seller/SellerInquiries'));
const SellerBookings = lazy(() => import('./pages/seller/SellerBookings'));
const SellerPropertyDetails = lazy(() => import('./pages/seller/SellerPropertyDetails'));
const BuyerDashboard = lazy(() => import('./pages/buyer/BuyerDashboard'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const BuyerForgotPassword = lazy(() => import('./pages/buyer/BuyerForgotPassword'));
const SellerForgotPassword = lazy(() => import('./pages/seller/SellerForgotPassword'));
const AgentForgotPassword = lazy(() => import('./pages/agent/AgentForgotPassword'));
const AdminForgotPassword = lazy(() => import('./pages/admin/AdminForgotPassword'));
const MyProperties = lazy(() => import('./pages/MyProperties'));
const AddProperty = lazy(() => import('./pages/AddProperty'));
const PropertyManagement = lazy(() => import('./pages/property-management/PropertyManagement'));
const RentProperty = lazy(() => import('./pages/property-management/RentProperty'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Loading component for Suspense fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

function AppRoutes() {
  // Safely get auth context - handle case where it might not be available yet
  let user, loading, getUserProfile;
  try {
    const auth = useAuth();
    user = auth.user;
    loading = auth.loading;
    getUserProfile = auth.getUserProfile;
  } catch (error) {
    // If useAuth fails, provide defaults
    console.warn('[AppRoutes] Auth context not available yet:', error);
    user = null;
    loading = true;
    getUserProfile = async () => null;
  }
  
  const navigate = useNavigate();
  const location = useLocation();

  // One-time Viswanath Serenity popup when app is opened (once per session) — hooks must be before any conditional return
  const [showViswanathPopup, setShowViswanathPopup] = useState(false);
  useEffect(() => {
    let alreadyShown = false;
    try {
      alreadyShown = !!sessionStorage.getItem(VISWANATH_POPUP_KEY);
    } catch {
      // private mode
    }
    if (alreadyShown) return;
    const t = setTimeout(() => setShowViswanathPopup(true), 600);
    return () => clearTimeout(t);
  }, []);

  const closeViswanathPopup = () => {
    try {
      sessionStorage.setItem(VISWANATH_POPUP_KEY, 'true');
    } catch {
      // ignore
    }
    setShowViswanathPopup(false);
  };

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname]);

  // Track SPA route changes in GA4 so all page traffic appears in one property.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const gtag = (window as any).gtag;
    if (typeof gtag !== 'function') return;

    const pagePath = `${location.pathname}${location.search || ''}${location.hash || ''}`;
    gtag('event', 'page_view', {
      page_path: pagePath,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [location.pathname, location.search, location.hash]);

  // Update meta tags based on current route
  useMetaTags(location.pathname);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* One-time Viswanath Serenity popup on app open */}
      {showViswanathPopup && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" role="dialog" aria-modal="true">
          <div className="relative max-w-2xl w-full">
            <img
              src={VISWANATH_SERENITY_IMAGE}
              alt="Viswanath Serenity"
              className="w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
            <button
              onClick={closeViswanathPopup}
              className="absolute top-3 right-3 p-1.5 rounded-full bg-white/90 hover:bg-white text-gray-700 shadow-md"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
      )}
      <WhatsAppFloatingButton />
      <Suspense fallback={<PageLoader />}>
        <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Home />} />
      <Route path="/buy" element={<Buy />} />
      <Route path="/rent" element={<Rent />} />
      <Route path="/sell" element={<Sell />} />
      <Route path="/agent" element={<Agent />} />
      <Route path="/about" element={<About />} />
      <Route path="/host" element={<Host />} />
      <Route path="/community" element={<Community />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-of-service" element={<TermsOfService />} />
      <Route path="/blogs" element={<Blogs />} />
      <Route path="/contact" element={<ContactUs />} />
      <Route path="/contact-us" element={<ContactUs />} />
      <Route path="/delete-account" element={<AccountDeletion />} />
      <Route path="/property/:id" element={<PropertyDetails />} />
      <Route path="/my-bookings" element={<ClientRouteGuard><MyBookings /></ClientRouteGuard>} />
      <Route path="/my-inquiries" element={<ClientRouteGuard><MyInquiries /></ClientRouteGuard>} />
      <Route path="/email-verification" element={<EmailVerification />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/buyer/forgot-password" element={<BuyerForgotPassword />} />
      <Route path="/seller/forgot-password" element={<SellerForgotPassword />} />
      <Route path="/agent/forgot-password" element={<AgentForgotPassword />} />
      <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/profile" element={<ClientRouteGuard><Profile /></ClientRouteGuard>} />
      <Route path="/wishlist" element={<ClientRouteGuard><Wishlist /></ClientRouteGuard>} />
      <Route path="/notifications" element={<ClientRouteGuard><Notifications /></ClientRouteGuard>} />
      <Route path="/agent/assignments" element={<AgentRouteGuard><AgentAssignments /></AgentRouteGuard>} />
      <Route path="/agent/dashboard/*" element={<AgentRouteGuard><AgentDashboard /></AgentRouteGuard>} />
      <Route path="/agent/dashboard" element={<AgentRouteGuard><AgentDashboard /></AgentRouteGuard>} />
      
          {/* Buyer Dashboard */}
          <Route path="/buyer/dashboard" element={<ClientRouteGuard allowedUserTypes={['buyer']}><BuyerDashboard /></ClientRouteGuard>} />
          
          {/* Seller Dashboard */}
          <Route path="/seller/dashboard" element={<ClientRouteGuard allowedUserTypes={['seller']}><SellerDashboard /></ClientRouteGuard>} />
          <Route path="/seller/inquiries" element={<ClientRouteGuard allowedUserTypes={['seller']}><SellerInquiries /></ClientRouteGuard>} />
          <Route path="/seller/bookings" element={<ClientRouteGuard allowedUserTypes={['seller']}><SellerBookings /></ClientRouteGuard>} />
          <Route path="/seller/add-property" element={<ClientRouteGuard allowedUserTypes={['seller']}><AddProperty /></ClientRouteGuard>} />
          <Route path="/seller/edit-property/:id" element={<ClientRouteGuard allowedUserTypes={['seller']}><AddProperty /></ClientRouteGuard>} />
          <Route path="/seller/property/:id" element={<ClientRouteGuard allowedUserTypes={['seller']}><SellerPropertyDetails /></ClientRouteGuard>} />

      {/* Property Management Routes */}
      <Route path="/property-management" element={<PropertyManagement />} />
      
      {/* Property Management Functional Routes - Require Seller/Agent/Admin Authentication */}
      <Route path="/my-properties" element={<ClientRouteGuard allowedUserTypes={['seller', 'agent', 'admin']}><MyProperties /></ClientRouteGuard>} />
      <Route path="/add-property" element={<ClientRouteGuard allowedUserTypes={['seller', 'agent', 'admin']}><AddProperty /></ClientRouteGuard>} />
      <Route path="/edit-property/:id" element={<ClientRouteGuard allowedUserTypes={['seller', 'agent', 'admin']}><AddProperty /></ClientRouteGuard>} />
      <Route path="/create-property" element={<ClientRouteGuard allowedUserTypes={['seller', 'agent', 'admin']}><AddProperty /></ClientRouteGuard>} />
      <Route path="/property-management/my-properties" element={<ClientRouteGuard allowedUserTypes={['seller', 'agent']}><MyProperties /></ClientRouteGuard>} />
      <Route path="/property-management/rent-property" element={<ClientRouteGuard allowedUserTypes={['seller', 'agent']}><RentProperty /></ClientRouteGuard>} />

      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<AdminRouteGuard><AdminDashboard /></AdminRouteGuard>} />
      <Route path="/admin/*" element={<AdminRouteGuard><AdminDashboard /></AdminRouteGuard>} />

      {/* Catch all route */}
      <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
  </>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AppRoutes />
    </ErrorBoundary>
  );
}

// Export AppRoutes separately for testing if needed
export { AppRoutes };

export default App;